/**
 * ゲームサーバー（描く側の GPU の台。EC2 の Name タグ synchro-game。Issue #881）の起動状況と、起動・停止（ダッシュボード）。
 *
 * - **補正サーバーとは別に起こす・止める**（/wake の game_status / game_start / game_stop / game_schedule。Lambda synchro-ec2wake）。
 *   会場の PC の描く側が駄目なときの保険（EC2 で演出を描いて補正サーバーへ返す）。ふだんは使わない
 * - **止める ＝ インスタンスごと消す**、**起動 ＝ 素の Deep Learning AMI から作り、synchro-client と Godot を入れて立てる**（AMI は焼かない。数分。Issue #882）。g5g.xlarge（T4G）で約 0.57 ドル/時（推測）なので確かめてから
 * - 起こすと既定 24 時間後に消す予約。補正サーバーに 180 分届かなくても自分で止まる
 * - 立つと本番へ名乗り、本番がいま使っている補正サーバーへ描いたものを送る（どこで動く補正サーバーでも。撮影タブの一覧に出る）
 * - 補正サーバーの台ではゲームを描かない（描くのは会場の PC か、このゲームサーバー）
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/api';
import { STOP_HOURS, stopText } from './LiveServerPanel';

const LABELS = {
  running: ['動いています', '#9be7a1'],
  pending: ['起動中…（描けるまで数分）', '#f0c36b'],
  stopping: ['停止中…（インスタンスごと消しています）', '#f0c36b'],
  stopped: ['止まっています（起動すると作ります。描けるまで数分）', '#8a93a8'],
};
const GAME_STOP_HOURS = 24;

export default function GameServerPanel() {
  const [state, setState] = useState(undefined);
  const [stopAt, setStopAt] = useState(null);
  const [hours, setHours] = useState(GAME_STOP_HOURS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const apply = (r) => {
    setState(r.game_state);
    if ('scheduled_stop' in r) setStopAt(r.scheduled_stop ?? null);
  };

  const refresh = useCallback(async () => {
    try {
      const r = await api.liveServer('game_status');
      setState(r.game_state);
      setStopAt(r.scheduled_stop ?? null);
      setError(null);
    } catch (e) {
      setError('状態を取れません');
    }
  }, []);

  useEffect(() => {
    let alive = true;
    let timer;
    const loop = async () => {
      await refresh();
      if (!alive) return;
      const s = stateRef.current;
      timer = setTimeout(loop, s === 'pending' || s === 'stopping' ? 3000 : 15000);
    };
    loop();
    return () => { alive = false; clearTimeout(timer); };
  }, [refresh]);

  const act = async (action) => {
    if (action === 'game_start'
      && !window.confirm(`ゲームサーバー（GPU の台 g5g.xlarge・約 0.57 ドル/時）を起動しますか？\n${hours} 時間後に自動で止まります。`)) return;
    if (action === 'game_stop' && !window.confirm('ゲームサーバーを止めますか？（描いている試合のゲーム映像が止まります）')) return;
    setBusy(true);
    try {
      apply(await api.liveServer(action, action === 'game_stop' ? {} : { hours }));
      setError(null);
    } catch (e) {
      setError(e?.message && e.message !== 'Live server request failed' ? e.message : '操作できませんでした');
    } finally {
      setBusy(false);
    }
  };

  const [label, color] = state === undefined ? ['確認中…', '#8a93a8'] : LABELS[state] ?? [state, '#8a93a8'];
  const up = state === 'running' || state === 'pending';
  return (
    <div className="card" data-testid="game-server-panel" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong className="card-title" style={{ margin: 0 }}>ゲームサーバー（EC2 の GPU の台）</strong>
        <span data-testid="game-server-state" style={{ color, fontWeight: 700 }}>● {label}</span>
        <span style={{ flex: 1 }} />
        {state === 'stopped' ? (
          <>
            <select value={hours} onChange={(e) => setHours(Number(e.target.value))} disabled={busy} data-testid="game-server-start-hours">
              {STOP_HOURS.map((h) => <option key={h} value={h}>{h} 時間後に止める</option>)}
            </select>
            <button type="button" className="btn" disabled={busy} onClick={() => act('game_start')} data-testid="game-server-start">起動</button>
          </>
        ) : null}
        {up ? <button type="button" className="btn" disabled={busy} onClick={() => act('game_stop')} data-testid="game-server-stop">停止</button> : null}
        <button type="button" className="btn" disabled={busy} onClick={refresh}>更新</button>
      </div>
      {up ? (
        <div style={{ marginTop: 6, fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', color: '#b8c0d4' }}>
          <span data-testid="game-server-stop-at">自動停止: {stopText(stopAt) ?? '予約なし'}</span>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="game-server-hours">
            {STOP_HOURS.map((h) => <option key={h} value={h}>今から {h} 時間後</option>)}
          </select>
          <button type="button" className="btn" disabled={busy} onClick={() => act('game_schedule')} data-testid="game-server-schedule">変更</button>
        </div>
      ) : null}
      {error ? <div style={{ marginTop: 6, color: '#ff8a8a', fontSize: 12 }}>{error}</div> : null}
    </div>
  );
}
